/* Un code-barres Code 39, dessiné à la main.
 *
 * ── Pourquoi pas une bibliothèque ───────────────────────────────────────────
 *
 * Code 39 tient dans une table de quarante-quatre entrées et une boucle. Une
 * dépendance pour cela, c'est un paquet de plus à suivre, à auditer et à
 * charger — sur une application qui doit s'ouvrir hors réseau, devant un
 * starter, avec une barre de signal vide.
 *
 * ── Pourquoi Code 39 et non Code 128 ────────────────────────────────────────
 *
 * Parce que c'est ce que la fédération imprime : le code de la carte physique,
 * décodé, est un Code 39. Une carte qui présenterait le même numéro dans une
 * autre symbologie serait peut-être lue — la plupart des douchettes acceptent
 * les deux — mais « peut-être » ne vaut rien au moment où l'on tend son
 * téléphone.
 *
 * ── Les zones silencieuses ──────────────────────────────────────────────────
 *
 * Un lecteur a besoin de blanc avant la première barre et après la dernière,
 * au moins dix modules. Sans elles, il ne trouve pas le début du code et ne
 * dit rien — pas d'erreur, juste un silence qu'on interprète comme un mauvais
 * angle. C'est la faute la plus fréquente des codes-barres faits maison, et
 * elle est ici comptée dans la largeur totale.
 */

/** Chaque caractère : neuf éléments, barre/espace alternés, `1` = large.
 *
 *  Le rapport large/fin doit rester entre 2 et 3 ; on prend 2,5, la valeur qui
 *  laisse le plus de marge des deux côtés de la tolérance. */
export const CODE39: Record<string, string> = {
    '0': '000110100', '1': '100100001', '2': '001100001', '3': '101100000',
    '4': '000110001', '5': '100110000', '6': '001110000', '7': '000100101',
    '8': '100100100', '9': '001100100', A: '100001001', B: '001001001',
    C: '101001000', D: '000011001', E: '100011000', F: '001011000',
    G: '000001101', H: '100001100', I: '001001100', J: '000011100',
    K: '100000011', L: '001000011', M: '101000010', N: '000010011',
    O: '100010010', P: '001010010', Q: '000000111', R: '100000110',
    S: '001000110', T: '000010110', U: '110000001', V: '011000001',
    W: '111000000', X: '010010001', Y: '110010000', Z: '011010000',
    '-': '010000101', '.': '110000100', ' ': '011000100', $: '010101000',
    '/': '010100010', '+': '010001010', '%': '000101010', '*': '010010100',
};

export const FIN = 1;
export const LARGE = 2.5;
/** Dix modules de blanc de chaque côté, le minimum de la norme. */
export const SILENCE = 10;

/** La suite des éléments d'un code, délimiteurs et espaces compris.
 *
 *  `0` = fin, `1` = large, une barre un élément sur deux en commençant par une
 *  barre. Rend `null` sur un caractère hors de l'alphabet.
 *
 *  SÉPARÉE du dessin, et exportée, pour une seule raison : c'est la partie qui
 *  peut être fausse sans que rien ne se voie. Un code-barres erroné ne
 *  ressemble pas à un code-barres cassé — il se scanne, et rend un autre
 *  numéro. Les tests l'attaquent donc directement, caractère par caractère. */
export function elementsCode39(valeur: string): string | null {
    // Les astérisques encadrent tout Code 39 : ce sont ses marques de départ et
    // d'arrêt, pas des caractères de données.
    const chaine = `*${valeur.toUpperCase()}*`;
    const morceaux: string[] = [];
    for (const [i, c] of [...chaine].entries()) {
        const motif = CODE39[c];
        if (motif === undefined) return null;
        morceaux.push(motif);
        // L'espace inter-caractère, toujours d'un module fin.
        if (i < chaine.length - 1) morceaux.push('0');
    }
    return morceaux.join('');
}

export function CodeBarres({ valeur, hauteur = 88, className }: {
    /** Le contenu, déjà normalisé en majuscules sans espaces. */
    valeur: string;
    hauteur?: number;
    className?: string;
}) {
    const elements = elementsCode39(valeur);
    if (elements == null) return null;

    const barres: { x: number; l: number }[] = [];
    let x = SILENCE;
    for (const [i, bit] of [...elements].entries()) {
        const l = bit === '1' ? LARGE : FIN;
        // Un élément sur deux est une barre, en commençant par une barre — et
        // les motifs faisant neuf éléments plus un espace de liaison, la parité
        // se conserve d'un caractère au suivant.
        if (i % 2 === 0) barres.push({ x, l });
        x += l;
    }
    const largeur = x + SILENCE;

    return (
        <svg viewBox={`0 0 ${largeur} ${hauteur}`} className={className}
            preserveAspectRatio="none" role="img"
            aria-label={`Code-barres du numéro ${valeur}`}>
            {/* Le fond BLANC est dessiné, il n'est pas hérité.
                Posé sur une carte teintée, un code-barres sans fond propre voit
                ses espaces prendre la couleur du support : le contraste tombe,
                et le lecteur décroche. */}
            <rect x={0} y={0} width={largeur} height={hauteur} fill="#fff" />
            {barres.map((b) => (
                <rect key={b.x} x={b.x} y={0} width={b.l} height={hauteur} fill="#000" />
            ))}
        </svg>
    );
}
